import {
  Column,
  FormRadioItemV2,
  FormRadioGroupV2,
  Typography,
} from '@/components'
import { findBusinessType } from '@/components/molecules/FormItem/FormAutocompleteItem'
import { AssessmentStepKeys, ThemeColor } from '@/enums'
import {
  useAssessmentValues,
  useSaveAssessmentStep,
} from '@/pages/SelfAssessmentPage/helpers'
import { useFormContext, useOnNextClick } from '@/providers'
import { themeColor } from '@/utils'
import { useCallback, useEffect } from 'react'
import styled from 'styled-components'
import { useBusinessTypeOptions } from './hooks/useBusinessTypeOptions'
import { useLocationTypes } from '@/services/locationTypes'
import { WhyDoWeAsk } from './components/WhyDoWeAsk/WhyDoWeAsk'
import { useSegment } from '@/modules'

interface FormValues {
  locationType?: string
}

interface BusinessTypeConfirmationAi {
  locationSubtypes: string[]
}

interface AssessmentValues {
  businessTypeConfirmationAi?: BusinessTypeConfirmationAi
  businessType?: {
    businessType: string
    locationType: string
    locationSubtypes: string[]
    locationTypeSpecification: string
  }
}

const FormWrapper = styled(Column)`
  gap: 24px;
`

const StepWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const RadioWrapper = styled.label<{
  selected?: boolean
}>`
  display: flex;
  align-items: center;
  width: 100%;
  border: 1px solid
    ${({ selected }) =>
      themeColor(selected ? ThemeColor.info1 : ThemeColor.b30)};

  border-radius: 8px;
  padding: 8px 16px;
  cursor: pointer;
  gap: 24px;
  margin-bottom: 16px;
`

const RadioLabelWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

export const BusinessTypeMainSelectionAiStep = () => {
  const { values } = useFormContext<FormValues>()
  const { save } = useSaveAssessmentStep(AssessmentStepKeys.businessType)
  const { options, isLoading: isOptionsLoading } = useBusinessTypeOptions()
  const {
    data: locationTypesData,
    isLoading: locationTypesLoading,
  } = useLocationTypes()
  const {
    values: allAssessmentValues,
    dispatchValues,
  } = useAssessmentValues() as {
    values: AssessmentValues
    dispatchValues: (values: AssessmentValues) => void
  }
  const locationSubtypesList =
    allAssessmentValues?.businessTypeConfirmationAi?.locationSubtypes ?? []
  const analytics = useSegment()

  useEffect(() => {
    if (values.locationType) {
      const optionData = options?.find(
        (option) => option.value === values.locationType,
      )
      if (optionData) {
        const businessType = findBusinessType(
          optionData,
          locationTypesData?.verticalsMap ?? {},
          locationTypesData?.getVerticalLabel,
        ).value
        const updatedLocationSubtypes = locationSubtypesList.filter(
          (item: string) => item !== values.locationType,
        )

        dispatchValues({
          ['businessType']: {
            businessType,
            locationType: values.locationType,
            locationSubtypes: updatedLocationSubtypes,
            locationTypeSpecification: '',
          },
        })
      }
    }
  }, [values?.locationType, options])

  const saveAndTrack = useCallback(async () => {
    await save()
    await analytics.track('business_type_main_risk_selection')
  }, [values])

  useOnNextClick(saveAndTrack)

  if (locationTypesLoading || isOptionsLoading) return null

  return (
    <StepWrapper>
      {
        <FormWrapper>
          <FormRadioGroupV2 name="locationType">
            {locationSubtypesList.map((item: string) => {
              const optionData = options?.find(
                (option) => option.value === item,
              )
              return (
                <RadioWrapper
                  key={item}
                  selected={values.locationType === item}
                >
                  <FormRadioItemV2 name="locationType" value={item} />
                  <RadioLabelWrapper>
                    <Typography responsive variant="p1Body">
                      {optionData?.label}
                    </Typography>
                    <Typography
                      color={ThemeColor.b50}
                      responsive
                      variant="badgeText"
                    >
                      {/* @ts-expect-error seems like an error, leaving it as is for now not to break the original logic during migration */}
                      {optionData?.subtitle}
                    </Typography>
                  </RadioLabelWrapper>
                </RadioWrapper>
              )
            })}
          </FormRadioGroupV2>
        </FormWrapper>
      }

      <WhyDoWeAsk />
    </StepWrapper>
  )
}
